import { useQuery } from "@tanstack/react-query";
import { GET_CMS_EDUCATOR_DOWNLOADS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { useMemo } from "react";
import { useProductFeatures } from "./useProductFeatures";

/**
 * Hook to fetch educator downloads
 * @param {Object} options - Query options
 * @param {number} options.cacheKey - Cache key for invalidating cached data
 */
export const useGetEducatorDownloads = ({ cacheKey = 1 } = {}) => {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["educatorDownloads", cacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_EDUCATOR_DOWNLOADS, {
        cacheKey,
      }),
    staleTime: 1000 * 60 * 15, // Consider data fresh for 15 minutes
    cacheTime: 1000 * 60 * 60 * 12,
  });

  useReactQueryErrorReporter("Could not load educator downloads", error, false);

  const { matchesProductPlans } = useProductFeatures();

  const filteredEducatorDownloads = useMemo(() => {
    const downloads = data?.cmsEducatorDownloads || [];
    return downloads.filter((download) => {
      // Check if the download's product plan matches user's plans
      return matchesProductPlans(
        download.productPlan?.map((plan) => plan.code),
      );
    });
  }, [data?.cmsEducatorDownloads, matchesProductPlans]);

  return {
    educatorDownloads: filteredEducatorDownloads,
    isLoading,
    error,
    refetch,
  };
};
